/**
 * Redux slice backing the EDGE-MONITOR-V2 Phase A admin endpoints. State is
 * keyed on `machineId` (and `siteAgentId` for the agent-detail entry) so the
 * Edge Live Monitor page — and any other view that wants the same data — can
 * select the freshest snapshot without re-fetching.
 *
 * Naming: store key is `machineGateway` per CONNECTIVITY_BACKEND_DEV_PROMPT.txt
 * "Frontend Patterns" — distinct from the existing `machinery` and
 * `machineProfile` slices.
 *
 * Stale-while-revalidate: each entry has its own `loading` flag, but the slice
 * never wipes prior data on a refresh — components use
 * `if (loading && !existingData)` to avoid blink-on-refetch (see prompt's
 * "No blink on refresh" rule).
 *
 * Phase A scope: data layer only. Polished tiles + audit table UI are
 * Frontend Dev's job after this deploys.
 */
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import edgeMonitorApi from "./edgeMonitorApi";
import {
  BotAuditLogPageDto,
  EdgeHubStatusDto,
  SiteAgentDetailDto,
  SymbolCatalogStatusDto,
} from "./models/edgeMonitorAdminModels";

// ---------------------------------------------------------------------------
// State

interface PerMachineState<T> {
  [machineId: string]: {
    data: T | null;
    loading: boolean;
    /** True when the server returned 404 / 403 / null (e.g. cross-tenant). */
    notAvailable: boolean;
    lastFetchedAt: string | null;
  };
}

interface PerSiteAgentState<T> {
  [siteAgentId: string]: {
    data: T | null;
    loading: boolean;
    notAvailable: boolean;
    lastFetchedAt: string | null;
  };
}

export interface MachineGatewayState {
  edgeHubStatus: PerMachineState<EdgeHubStatusDto>;
  symbolCatalogStatus: PerMachineState<SymbolCatalogStatusDto>;
  botAuditLog: PerMachineState<BotAuditLogPageDto>;
  siteAgentDetail: PerSiteAgentState<SiteAgentDetailDto>;
}

const initialState: MachineGatewayState = {
  edgeHubStatus: {},
  symbolCatalogStatus: {},
  botAuditLog: {},
  siteAgentDetail: {},
};

// ---------------------------------------------------------------------------
// Helpers

function ensureMachineEntry<T>(
  bucket: PerMachineState<T>,
  machineId: string
): void {
  if (!bucket[machineId]) {
    bucket[machineId] = {
      data: null,
      loading: false,
      notAvailable: false,
      lastFetchedAt: null,
    };
  }
}

function ensureAgentEntry<T>(
  bucket: PerSiteAgentState<T>,
  siteAgentId: string
): void {
  if (!bucket[siteAgentId]) {
    bucket[siteAgentId] = {
      data: null,
      loading: false,
      notAvailable: false,
      lastFetchedAt: null,
    };
  }
}

// ---------------------------------------------------------------------------
// Async thunks

export const fetchEdgeHubStatus = createAsyncThunk(
  "machineGateway/fetchEdgeHubStatus",
  async (machineId: string) => {
    const data = await edgeMonitorApi.getEdgeHubStatus(machineId);
    return { machineId, data };
  }
);

export const fetchSymbolCatalogStatus = createAsyncThunk(
  "machineGateway/fetchSymbolCatalogStatus",
  async (machineId: string) => {
    const data = await edgeMonitorApi.getSymbolCatalogStatus(machineId);
    return { machineId, data };
  }
);

export const fetchBotAuditLog = createAsyncThunk(
  "machineGateway/fetchBotAuditLog",
  async (args: { machineId: string; limit?: number }) => {
    const data = await edgeMonitorApi.getBotAuditLog(
      args.machineId,
      args.limit ?? 50
    );
    return { machineId: args.machineId, data };
  }
);

export const fetchSiteAgentDetail = createAsyncThunk(
  "machineGateway/fetchSiteAgentDetail",
  async (siteAgentId: string) => {
    const data = await edgeMonitorApi.getSiteAgentDetail(siteAgentId);
    return { siteAgentId, data };
  }
);

// ---------------------------------------------------------------------------
// Slice

const machineGatewaySlice = createSlice({
  name: "machineGateway",
  initialState,
  reducers: {
    /** Clear all per-machine entries. Used on logout / customer switch. */
    clearAll: () => initialState,
  },
  extraReducers: (builder) => {
    // ---------- edge hub status
    builder
      .addCase(fetchEdgeHubStatus.pending, (state, action) => {
        const machineId = action.meta.arg;
        ensureMachineEntry(state.edgeHubStatus, machineId);
        state.edgeHubStatus[machineId].loading = true;
      })
      .addCase(
        fetchEdgeHubStatus.fulfilled,
        (
          state,
          action: PayloadAction<{
            machineId: string;
            data: EdgeHubStatusDto | null;
          }>
        ) => {
          const { machineId, data } = action.payload;
          ensureMachineEntry(state.edgeHubStatus, machineId);
          state.edgeHubStatus[machineId].loading = false;
          state.edgeHubStatus[machineId].data = data;
          state.edgeHubStatus[machineId].notAvailable = data == null;
          state.edgeHubStatus[machineId].lastFetchedAt = new Date().toISOString();
        }
      )
      .addCase(fetchEdgeHubStatus.rejected, (state, action) => {
        const machineId = action.meta.arg;
        ensureMachineEntry(state.edgeHubStatus, machineId);
        state.edgeHubStatus[machineId].loading = false;
        // Keep prior data per stale-while-revalidate semantics; flag the
        // failure on `notAvailable` so the UI can choose to render a stale
        // marker.
        state.edgeHubStatus[machineId].notAvailable = true;
      });

    // ---------- symbol catalog status
    builder
      .addCase(fetchSymbolCatalogStatus.pending, (state, action) => {
        const machineId = action.meta.arg;
        ensureMachineEntry(state.symbolCatalogStatus, machineId);
        state.symbolCatalogStatus[machineId].loading = true;
      })
      .addCase(
        fetchSymbolCatalogStatus.fulfilled,
        (
          state,
          action: PayloadAction<{
            machineId: string;
            data: SymbolCatalogStatusDto | null;
          }>
        ) => {
          const { machineId, data } = action.payload;
          ensureMachineEntry(state.symbolCatalogStatus, machineId);
          state.symbolCatalogStatus[machineId].loading = false;
          state.symbolCatalogStatus[machineId].data = data;
          state.symbolCatalogStatus[machineId].notAvailable = data == null;
          state.symbolCatalogStatus[machineId].lastFetchedAt = new Date().toISOString();
        }
      )
      .addCase(fetchSymbolCatalogStatus.rejected, (state, action) => {
        const machineId = action.meta.arg;
        ensureMachineEntry(state.symbolCatalogStatus, machineId);
        state.symbolCatalogStatus[machineId].loading = false;
        state.symbolCatalogStatus[machineId].notAvailable = true;
      });

    // ---------- bot audit log
    builder
      .addCase(fetchBotAuditLog.pending, (state, action) => {
        const machineId = action.meta.arg.machineId;
        ensureMachineEntry(state.botAuditLog, machineId);
        state.botAuditLog[machineId].loading = true;
      })
      .addCase(
        fetchBotAuditLog.fulfilled,
        (
          state,
          action: PayloadAction<{
            machineId: string;
            data: BotAuditLogPageDto | null;
          }>
        ) => {
          const { machineId, data } = action.payload;
          ensureMachineEntry(state.botAuditLog, machineId);
          state.botAuditLog[machineId].loading = false;
          state.botAuditLog[machineId].data = data;
          state.botAuditLog[machineId].notAvailable = data == null;
          state.botAuditLog[machineId].lastFetchedAt = new Date().toISOString();
        }
      )
      .addCase(fetchBotAuditLog.rejected, (state, action) => {
        const machineId = action.meta.arg.machineId;
        ensureMachineEntry(state.botAuditLog, machineId);
        state.botAuditLog[machineId].loading = false;
        state.botAuditLog[machineId].notAvailable = true;
      });

    // ---------- site agent detail
    builder
      .addCase(fetchSiteAgentDetail.pending, (state, action) => {
        const siteAgentId = action.meta.arg;
        ensureAgentEntry(state.siteAgentDetail, siteAgentId);
        state.siteAgentDetail[siteAgentId].loading = true;
      })
      .addCase(
        fetchSiteAgentDetail.fulfilled,
        (
          state,
          action: PayloadAction<{
            siteAgentId: string;
            data: SiteAgentDetailDto | null;
          }>
        ) => {
          const { siteAgentId, data } = action.payload;
          ensureAgentEntry(state.siteAgentDetail, siteAgentId);
          state.siteAgentDetail[siteAgentId].loading = false;
          state.siteAgentDetail[siteAgentId].data = data;
          state.siteAgentDetail[siteAgentId].notAvailable = data == null;
          state.siteAgentDetail[siteAgentId].lastFetchedAt = new Date().toISOString();
        }
      )
      .addCase(fetchSiteAgentDetail.rejected, (state, action) => {
        const siteAgentId = action.meta.arg;
        ensureAgentEntry(state.siteAgentDetail, siteAgentId);
        state.siteAgentDetail[siteAgentId].loading = false;
        state.siteAgentDetail[siteAgentId].notAvailable = true;
      });
  },
});

export const { clearAll } = machineGatewaySlice.actions;
export default machineGatewaySlice.reducer;

// ---------------------------------------------------------------------------
// Selectors — typed against RootState by the consumer (avoids circular import
// between this slice file and core/store.ts).

/** Read the cached edge-hub-status entry for a machine, or undefined if never fetched. */
export const selectEdgeHubStatusEntry =
  (state: { machineGateway: MachineGatewayState }, machineId: string) =>
    state.machineGateway.edgeHubStatus[machineId];

export const selectSymbolCatalogStatusEntry =
  (state: { machineGateway: MachineGatewayState }, machineId: string) =>
    state.machineGateway.symbolCatalogStatus[machineId];

export const selectBotAuditLogEntry =
  (state: { machineGateway: MachineGatewayState }, machineId: string) =>
    state.machineGateway.botAuditLog[machineId];

export const selectSiteAgentDetailEntry =
  (state: { machineGateway: MachineGatewayState }, siteAgentId: string) =>
    state.machineGateway.siteAgentDetail[siteAgentId];
